import { Capacitor } from '@capacitor/core';

/**
 * Desktop notifications for the incoming-call card (the browser's
 * Notification API). Asked for only when the person presses "Turn on desktop
 * notifications" on /sales, never on page load: a browser that is asked
 * unprompted tends to be told no for good.
 *
 * The app on a phone has none of this (a web view has no Notification API);
 * the in-app card still shows there.
 */

const PREF_KEY = 'sales_desktop_notifications';

export function desktopNotifySupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && !Capacitor.isNativePlatform();
}

export type DesktopNotifyState = 'unsupported' | 'off' | 'on' | 'blocked';

export function desktopNotifyState(): DesktopNotifyState {
  if (!desktopNotifySupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  let pref: string | null = null;
  try { pref = localStorage.getItem(PREF_KEY); } catch { /* private window */ }
  return Notification.permission === 'granted' && pref !== 'off' ? 'on' : 'off';
}

/**
 * The browser's answer. Older Safari only takes a callback, and a browser that
 * shows its quiet prompt (Chrome's crossed-out bell in the address bar) or
 * auto-blocks after earlier dismissals answers 'default' without asking, so
 * the caller has to say something rather than look like nothing happened.
 */
function askPermission(): Promise<NotificationPermission> {
  return new Promise((resolve) => {
    try {
      const p = Notification.requestPermission((answer) => resolve(answer));
      if (p && typeof p.then === 'function') p.then(resolve, () => resolve('default'));
    } catch {
      resolve('default');
    }
  });
}

export interface TurnOnResult {
  state: DesktopNotifyState;
  /** 'default' = the browser gave no answer (prompt dismissed, hidden or suppressed). */
  answer: NotificationPermission | 'unsupported';
}

/** Asks the browser, and remembers the choice on this device. */
export async function turnOnDesktopNotify(): Promise<TurnOnResult> {
  if (!desktopNotifySupported()) return { state: 'unsupported', answer: 'unsupported' };
  const answer = Notification.permission === 'granted' ? 'granted' : await askPermission();
  try { localStorage.setItem(PREF_KEY, answer === 'granted' ? 'on' : 'off'); } catch { /* private window */ }
  const state = desktopNotifyState();
  // A test one, so the person sees it works (or that their OS hides them).
  if (state === 'on') desktopNotify('Desktop notifications are on', 'You will be told here about incoming calls.', 'sales-notify-test');
  return { state, answer };
}

export function turnOffDesktopNotify(): DesktopNotifyState {
  try { localStorage.setItem(PREF_KEY, 'off'); } catch { /* private window */ }
  return desktopNotifyState();
}

/** Shows one, if switched on. `tag` replaces an earlier one for the same call. */
export function desktopNotify(title: string, body: string, tag: string, onClick?: () => void): Notification | null {
  if (desktopNotifyState() !== 'on') return null;
  try {
    const n = new Notification(title, { body, tag });
    n.onclick = () => {
      window.focus();
      onClick?.();
      n.close();
    };
    return n;
  } catch {
    return null;
  }
}
